import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useAddSkillMutation,
  useUpdateSkillMutation,
} from "@/hooks/api/profile.queries";
import {
  SkillPayloadType,
  SkillType,
} from "@/services/profile.service";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";

const formSchema = z.object({
  name: z.string().min(1, "Skill name is required"),
  proficiency: z.string().min(1, "Proficiency is required"),
  years_of_experience: z.number().min(0, "Years must be >= 0"),
});

type FormValues = z.infer<typeof formSchema>;

interface SkillDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onCancel: () => void;
  mode: "create" | "edit";
  skillId?: string;
  skillData?: SkillType | null;
}

const SkillDialog = ({
  isOpen,
  onOpenChange,
  onCancel,
  mode,
  skillId,
  skillData,
}: SkillDialogProps) => {
  const isEdit = mode === "edit";

  const addMutation = useAddSkillMutation();
  const updateMutation = useUpdateSkillMutation();

  const isPending =
    addMutation.isPending || updateMutation.isPending;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      proficiency: "",
      years_of_experience: 0,
    },
  });

  useEffect(() => {
    if (isOpen) {
      if (isEdit && skillData) {
        form.reset({
          name: skillData.name,
          proficiency: skillData.proficiency,
          years_of_experience:
            skillData.years_of_experience,
        });
      } else {
        form.reset({
          name: "",
          proficiency: "",
          years_of_experience: 0,
        });
      }
    }
  }, [isOpen, isEdit, skillData, form]);

  const onSubmit = (values: FormValues) => {
    const payload: SkillPayloadType = {
      name: values.name,
      proficiency: values.proficiency,
      years_of_experience: values.years_of_experience,
    };

    if (isEdit && skillId) {
      updateMutation.mutate(
        { id: skillId, data: payload },
        { onSuccess: onCancel }
      );
    } else {
      addMutation.mutate(payload, {
        onSuccess: onCancel,
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit Skill" : "Add Skill"}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update your existing skill details."
              : "Add a new skill to your profile."}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4"
          >
            {/* Skill Name */}
            <FormField
              control={form.control as any}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Skill Name *</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g. React, Python"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Proficiency */}
            <FormField
              control={form.control as any}
              name="proficiency"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Proficiency *</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select proficiency" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="beginner">
                        Beginner
                      </SelectItem>
                      <SelectItem value="intermediate">
                        Intermediate
                      </SelectItem>
                      <SelectItem value="advanced">
                        Advanced
                      </SelectItem>
                      <SelectItem value="expert">
                        Expert
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Years of Experience */}
            <FormField
              control={form.control as any}
              name="years_of_experience"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Years of Experience *
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="e.g. 2"
                      value={field.value ?? ""}
                      onChange={(e) =>
                        field.onChange(e.target.value === "" ? 0 : Number(e.target.value))
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Buttons */}
            <DialogFooter className="mt-4 flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isPending}
              >
                {isPending
                  ? "Saving..."
                  : isEdit
                  ? "Update Skill"
                  : "Add Skill"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default SkillDialog;